import type { BaseDataPoint } from "../charts";

/** The camera: the visible time window, absolute epoch ms. */
export type Viewport = { startMs: number; endMs: number };

/** One rung of the resolution ladder. */
export type Tier = { id: string; bucketMs: number; tileSpanMs: number };
export type ResolutionLadder = Tier[];

/** A fixed-grid data slice: tile `index` at `tierId`, covering [startMs, endMs). */
export type TileRef = { tierId: string; index: number; startMs: number; endMs: number };

/** Data a tile carries. `band` optional → engine is metric-agnostic.
 * `lines` carries additional named series (e.g. export/net flows) that
 * stitch with the same dedup-by-timestamp semantics as `line`. */
export type TileData = { line: BaseDataPoint[]; band?: BaseDataPoint[]; lines?: Record<string, BaseDataPoint[]> };

/** Injected loader: one tile = one bounded fetch. */
export type FetchTileContext = {
  reason: "load" | "refresh";
  /** Check before adapter side effects such as shared-cache writes. */
  isCurrent?: () => boolean;
};
export type FetchTile = (
  tier: Tier,
  startMs: number,
  endMs: number,
  signal: AbortSignal,
  context?: FetchTileContext
) => Promise<TileData>;

export const tileKey = (tierId: string, index: number): string => `${tierId}:${index}`;

export const hasTileData = (data?: TileData): boolean =>
  !!data &&
  (data.line.length > 0 ||
    (data.band?.length ?? 0) > 0 ||
    Object.values(data.lines ?? {}).some((points) => points.length > 0));
